import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';

import { Eye } from '@/core/tamagoshi/icons/Eye';
import { Text } from '@/core/tamagoshi/Text/Text';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { Link } from '@/domains/core/routing/components/Link';
import { Typography } from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { InfoDoc } from '@/domains/productDiscovery/ProductPage/b2c/components/DescriptionBlock/components/InfoDoc/InfoDoc';
import type { InfoDocsModalProps } from '@/domains/productDiscovery/ProductPage/b2c/components/DescriptionBlock/components/InfoDocsModal/InfoDocsModal';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';
import type { Uri } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';

import {
  DESCRIPTION_FILE_KEY,
  descriptionPanelDownloadDocumentTitle,
  docFileNames,
  downloadTheDocuments,
  SAFETY_FILE_KEY,
} from './translations';

import styles from './infoDocs.module.scss';

interface Doc {
  key: string;
  url: string;
  name: string;
}

const InfoDocsModal = dynamic<InfoDocsModalProps>(
  () =>
    import(
      '@/domains/productDiscovery/ProductPage/b2c/components/DescriptionBlock/components/InfoDocsModal/InfoDocsModal'
    ).then((mod) => mod.InfoDocsModal),
  { ssr: true },
);

interface Props {
  descriptionFileUrl?: Uri;
  safetyInformationPdf?: string;
  infoDocs?: { key: string; url: string }[];
}

export const InfoDocs: FunctionComponent<Props> = ({
  infoDocs = [],
  descriptionFileUrl,
  safetyInformationPdf,
}) => {
  const allDocs: Doc[] = infoDocs.map((doc) => ({
    ...doc,
    name: docFileNames[doc.key as keyof typeof docFileNames],
  }));

  if (descriptionFileUrl) {
    allDocs.unshift({
      key: DESCRIPTION_FILE_KEY,
      url: descriptionFileUrl,
      name: docFileNames[DESCRIPTION_FILE_KEY as keyof typeof docFileNames],
    });
  }

  if (safetyInformationPdf) {
    allDocs.push({
      key: SAFETY_FILE_KEY,
      url: safetyInformationPdf,
      name: docFileNames[SAFETY_FILE_KEY as keyof typeof docFileNames],
    });
  }

  const { asPath } = useRouter();
  const [isProductPageRevampEnabled] = useBooleanFlags([
    StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
  ]);

  if (!allDocs.length) return null;
  return (
    <>
      {isProductPageRevampEnabled && (
        <Typography scale="body1" weight="bold">
          {descriptionPanelDownloadDocumentTitle}
        </Typography>
      )}
      <div className={styles.container}>
        {/* Mobile see all */}

        {allDocs.length > 1 && (
          <Link
            href="#all-docs"
            shallow
            data-skipdownloadall
            data-testid="link-for-all-docs-modal"
            className={styles.openModalLink}
          >
            <div className={styles.openModalCTA}>
              <Eye width="16px" height="16px" color="#505971" />
              <Text>{downloadTheDocuments}</Text>
            </div>
          </Link>
        )}

        {/* Desktop one by one (+Mobile, if only 1 doc) */}
        {allDocs.map((doc) => (
          <InfoDoc
            url={doc.url}
            name={doc.name}
            className={classnames({
              [styles.infoDocMultipleDocs]: allDocs.length > 1,
            })}
            key={`${doc.url}-${doc.name}`}
          />
        ))}
      </div>

      {/* Mobile modal */}
      {asPath.includes('#all-docs') && <InfoDocsModal docs={allDocs} />}
    </>
  );
};

InfoDocs.displayName = 'InfoDocs';
